import type { ReactNode } from 'react';
import { routineSteps, skinTypes } from '../../data/concerns';
import { useCatalog } from '../../contexts/CatalogContext';
import type { RoutineStep, SkinType } from '../../core/types/catalog';
import { chipCls } from '../../ui/styles';
type Props = {
  skin: SkinType | null;
  setSkin: (s: SkinType | null) => void;
  step: RoutineStep | null;
  setStep: (s: RoutineStep | null) => void;
  /** `null` = alle merken. */
  brand: string | null;
  setBrand: (b: string | null) => void;
  /** Op mobiel staat huidtype al als chips boven de producten. */
  showSkin?: boolean;
};
function Chip({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return <button type="button" aria-pressed={active} onClick={onClick} className={chipCls(active)} data-id="element-6957" data-mp="3uph7ogpjal2r">
      {children}
    </button>;
}

/** Huidtype als rij chips (mobiel, boven de producten). */
export function SkinChips({
  skin,
  setSkin
}: Pick<Props, 'skin' | 'setSkin'>) {
  return <div role="group" aria-label="Huidtype" className="contents" data-id="element-6958" data-mp="1ydp0idh0lknz">
      <Chip active={skin === null} onClick={() => setSkin(null)} data-id="element-6959" data-mp="1tfx7fg4mvsi7">
        Alle huidtypes
      </Chip>
      {skinTypes.map(s => <Chip key={s} active={skin === s} onClick={() => setSkin(skin === s ? null : s)} data-id="element-6960" data-mp="283iaja1idrq4">
          {s}
        </Chip>)}
    </div>;
}

/** Alle filters: huidtype (desktop), stap in de routine en merk. */
export function FilterPanel({
  skin,
  setSkin,
  step,
  setStep,
  brand,
  setBrand,
  showSkin = true
}: Props) {
  const {
    brands
  } = useCatalog();
  return <div className="space-y-9" data-id="element-6961" data-mp="1ogauz8wmqgag">
      {showSkin && <fieldset data-id="element-6962" data-mp="1oi42eqf2h4ji">
          <legend className="text-[13px] text-ink" data-id="element-6963" data-mp="1oegblz10aw8h">Huidtype</legend>
          <ul className="mt-3 border-t border-line" data-id="element-6964" data-mp="1ke65mmd7s5tm">
            {skinTypes.map(s => {
          const isActive = skin === s;
          return <li key={s} className="border-b border-line" data-id="element-6965" data-mp="2vue7elktnd48">
                  <button type="button" aria-pressed={isActive} onClick={() => setSkin(isActive ? null : s)} className={`flex w-full items-center justify-between py-3 text-left text-[15px] transition-colors duration-150 ease-soft ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-6966" data-mp="2vw76nft3uv27">
                    {s}
                    <span aria-hidden="true" className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-clay' : 'bg-transparent'}`} data-id="element-6967" data-mp="19tihfngpgjvv" />
                  </button>
                </li>;
        })}
          </ul>
        </fieldset>}

      <fieldset data-id="element-6968" data-mp="1irau0jv1ldfk">
        <legend className="text-[13px] text-ink" data-id="element-6969" data-mp="1nk3z58ufans6">Stap in je routine</legend>
        <div className="mt-3 flex flex-wrap gap-2" data-id="element-6970" data-mp="1nlf61hj60bni">
          {routineSteps.map(s => <Chip key={s} active={step === s} onClick={() => setStep(step === s ? null : s)} data-id="element-6971" data-mp="r1zc39z7ktqo">
              {s}
            </Chip>)}
        </div>
      </fieldset>

      <fieldset data-id="element-6972" data-mp="nj8ytmzmon9m">
        <legend className="text-[13px] text-ink" data-id="element-6973" data-mp="njs5u8nqpr8v">Merk</legend>
        <div className="mt-3 flex flex-wrap gap-2" data-id="element-6974" data-mp="ikpcvml1bbh9">
          <Chip active={brand === null} onClick={() => setBrand(null)} data-id="element-6975" data-mp="ij4flp7c5efp">
            Alle merken
          </Chip>
          {brands.map(b => <Chip key={b.name} active={brand === b.name} onClick={() => setBrand(brand === b.name ? null : b.name)} data-id="element-6976" data-mp="9l2aaxgkjzmm">
              {b.name}
            </Chip>)}
        </div>
      </fieldset>
    </div>;
}